// The scale wording lives in `config/` so Python reads the same file
// (`apps/python-api/services/scale_wording.py`): the python-api image and
// Argo ship `config/`, never `packages/shared-types/src/`.
import scaleWordingJson from "../../../../config/questionnaire-scale-wording.json";

export type ScaleWordingLanguage = keyof typeof scaleWordingJson;
export type ScaleWordingFamily = keyof (typeof scaleWordingJson)["en"];

export const SCALE_WORDING = scaleWordingJson;

/**
 * The languages a survey can be worded in: the scale-wording packs' languages,
 * in file order (Python's `SURVEY_LANGUAGES` reads the same keys).
 */
export const SURVEY_LANGUAGES = Object.freeze(
  Object.keys(scaleWordingJson) as Array<ScaleWordingLanguage>,
);

export function scaleWordingFor(language: string): (typeof scaleWordingJson)["en"] {
  const code = language.trim().toLowerCase().split(/[-_]/)[0];
  if (code in scaleWordingJson) {
    return scaleWordingJson[code as ScaleWordingLanguage];
  }
  return scaleWordingJson.en;
}

/**
 * The respondent instructions the compiler writes around a question (response
 * guidance, Yes/No, the default N/A label) and the field labels of a tested
 * item's description (`stimulus*`: its price, features and packaging), in the
 * study's content language. Templates fill `{name}` slots with
 * `fillRespondentWording`; `{one, other}` pairs are plural forms.
 */
export type RespondentWording = (typeof scaleWordingJson)["en"]["respondentGuidance"] & {
  yes: string;
  no: string;
};

export function respondentWordingFor(language: string | undefined): RespondentWording {
  const pack = scaleWordingFor(language ?? "en");
  return { ...pack.respondentGuidance, yes: pack.yes, no: pack.no };
}

/** Fills a respondent wording template's `{name}` slots. */
export function fillRespondentWording(
  template: string,
  values: Record<string, string | number> = {},
): string {
  return template.replace(/\{(\w+)\}/g, (slot, name: string) =>
    name in values ? String(values[name]) : slot,
  );
}

/**
 * Blank answer-option hint in the study content language (`Option {index}`).
 * Cache scope: one-shot, not cached (pure lookup).
 */
export function optionPlaceholderLabel(
  language: string | undefined,
  index: number,
): string {
  return fillRespondentWording(respondentWordingFor(language).optionPlaceholder, {
    index,
  });
}

/** Respondent-facing duration unit (`hours` → `horas`). Unknown units stay as stored. */
export function durationUnitLabel(
  language: string | undefined,
  unit: string | null | undefined,
): string {
  const code = (unit ?? "").trim();
  if (!code) {
    return "";
  }
  const units = respondentWordingFor(language).durationUnits;
  if (Object.prototype.hasOwnProperty.call(units, code)) {
    return units[code as keyof typeof units];
  }
  return code;
}

/**
 * Editor preview of a continuous bound, in the study content language.
 * `maxLabel` absent means the upper bound is open (`Range: At least …`).
 */
export function continuousRangeHint(
  language: string | undefined,
  minLabel: string,
  maxLabel?: string,
): string {
  const wording = respondentWordingFor(language);
  if (maxLabel === undefined) {
    return fillRespondentWording(wording.rangeHintAtLeast, { bound: minLabel });
  }
  return fillRespondentWording(wording.rangeHintBetween, {
    min: minLabel,
    max: maxLabel,
  });
}

/** Residual open-end bucket title and description, in the study content language. */
export function otherMixedCopy(language: string | undefined): {
  title: string;
  description: string;
} {
  const wording = respondentWordingFor(language);
  return {
    title: wording.otherMixedTitle,
    description: wording.otherMixedDescription,
  };
}

/** Every residual-bucket title the wording packs publish, including English. */
export function otherMixedTitles(): Array<string> {
  const titles = new Set<string>();
  for (const language of SURVEY_LANGUAGES) {
    const title = scaleWordingJson[language].respondentGuidance.otherMixedTitle;
    if (title.trim()) {
      titles.add(title);
    }
  }
  return [...titles];
}

/**
 * The "not applicable" choice's text: its own label, or the default N/A label
 * of the study language's respondent wording (`respondentWordingFor`). The
 * one rule: the compiler writes this text as the N/A choice and in the rating
 * guidance, and the builder shows it.
 */
export function notApplicableText(
  label: unknown,
  wording: Pick<RespondentWording, "notApplicableLabel">,
): string {
  return typeof label === "string" && label.trim().length > 0
    ? label
    : wording.notApplicableLabel;
}

export function scaleFamilyLabels(
  family: string,
  language: string,
): Array<string> | undefined {
  const pack = scaleWordingFor(language) as Record<string, unknown>;
  const labels = pack[family];
  return Array.isArray(labels) ? labels : undefined;
}
